import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeader, SourceTag, ConfidenceBar, stripMarkdown } from './shared'
import type { TreeProps } from './shared'
import type { RequirementsTree } from '@ispot/shared'

export function VisionTree({ tree, search, expandedSections, toggleSection }: TreeProps) {
  const [expandedStatement, setExpandedStatement] = useState(false)
  const sections = buildVisionSections(tree)

  return (
    <div>
      <div className="grid grid-cols-[1fr_90px] px-4 py-1.5 bg-warm-100 border-b border-border text-[10px] font-semibold text-text-muted uppercase tracking-wider">
        <div>Name</div>
        <div>Source</div>
      </div>

      {sections.map((section, i) => {
        const sectionKey = `v-${i}`
        const isOpen = expandedSections.has(sectionKey) || expandedSections.size === 0
        const filteredItems = section.items.filter(item =>
          !search || item.label.toLowerCase().includes(search.toLowerCase())
        )

        return (
          <div key={sectionKey}>
            <SectionHeader num={i + 1} title={section.title} count={filteredItems.length} isOpen={isOpen} onToggle={() => toggleSection(sectionKey)} />
            {isOpen && filteredItems.map((item, j) => {
              // Statement gets special multi-line rendering
              if (item.type === 'Statement') {
                const plain = stripMarkdown(item.label)
                const isLong = plain.length > 200
                const displayText = isLong && !expandedStatement ? plain.slice(0, 200) + '...' : plain
                return (
                  <div key={j} className="pl-10 pr-4 py-3 border-b border-border-subtle">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <span className="text-[11px] font-medium text-text-muted">Statement</span>
                        <p className="text-[13px] text-text-secondary leading-relaxed mt-1 whitespace-pre-line">
                          {displayText}
                        </p>
                        {isLong && (
                          <button
                            onClick={() => setExpandedStatement(!expandedStatement)}
                            className="text-[12px] text-solution font-medium mt-1 hover:underline"
                          >
                            {expandedStatement ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                      <div className="flex-shrink-0">{item.source && <SourceTag source={item.source} />}</div>
                    </div>
                  </div>
                )
              }

              return (
                <div key={j} className={cn(
                  'grid grid-cols-[1fr_90px] px-4 py-2.5 border-b border-border-subtle items-center hover:bg-warm-50 transition-colors',
                  item.indent === 2 && 'pl-10',
                  item.indent === 3 && 'pl-16',
                )}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-text-muted min-w-[70px]">{item.type}</span>
                    <span className="text-[13.5px] font-medium">
                      {item.label}
                      {item.risk && (
                        <span className="inline-flex items-center gap-0.5 ml-2 text-[11px] text-red-500 font-normal">
                          <AlertTriangle className="w-3 h-3" /> At risk
                        </span>
                      )}
                    </span>
                    {item.confidence !== undefined && (
                      <ConfidenceBar value={item.confidence} links={item.evidenceLinks || 0} />
                    )}
                  </div>
                  <div>{item.source && <SourceTag source={item.source} />}</div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

type VisionItem = { type: string; label: string; source?: 'Decided' | 'Discovered' | 'Inferred'; indent: number; risk?: boolean; confidence?: number; evidenceLinks?: number }

function buildVisionSections(tree: RequirementsTree): { title: string; items: VisionItem[] }[] {
  return [
    {
      title: 'Business Context',
      items: [
        { type: 'Vision', label: tree.vision.name, source: 'Decided', indent: 2 },
        ...(tree.vision.visionStatement ? [{ type: 'Statement', label: tree.vision.visionStatement, source: 'Decided' as const, indent: 2 }] : []),
      ],
    },
    {
      title: 'Solution Design',
      items: tree.capabilities.flatMap(({ capability, features }) => [
        { type: 'Capability', label: capability.name, source: 'Decided' as const, indent: 2 },
        ...features.map(f => ({
          type: 'Feature', label: f.name, source: 'Decided' as const, indent: 3,
          confidence: f.evidenceConfidence,
          evidenceLinks: f.linkedPains.length + f.linkedGains.length + f.linkedJtbds.length,
          risk: (f.evidenceConfidence || 0) < 0.4,
        })),
      ]),
    },
  ]
}
